import React, { useEffect, useRef } from "react";
import { Tooltip } from "antd";
import {
  BellOutlined,
  CheckSquareOutlined,
  CommentOutlined,
  ContactsOutlined,
  FileTextOutlined,
  MessageOutlined,
  MoonOutlined,
  ProjectOutlined,
  SolutionOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileMenu from "../Header/ProfileMenu";
import EconnectLogo from "../../assets/images/Logo(2).png";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { localStorageUtil } from "../../utils/CommonFunctions";
import "./Rail.scss";

const CHAT_PATH = /^\/elsnertech\/(message|channels)\//;

// Icons for the product notification channels, by name; others get a bell
const PRODUCT_ICONS = {
  hrms: <SolutionOutlined />,
  pms: <ProjectOutlined />,
  crm: <ContactsOutlined />,
  taskhub: <CheckSquareOutlined />,
};

const getProductIcon = (name = "") =>
  PRODUCT_ICONS[name.toLowerCase().replace(/[^a-z]/g, "")] || <BellOutlined />;

// Where "Chats" goes back to: the last chat opened in this tab, else the last active one
const getFallbackChatPath = () => {
  const lastActiveChat = localStorageUtil.getItem("userData")?.lastActiveChat;
  if (lastActiveChat?.id) {
    const isChannel = ["channel", "channels"].includes(lastActiveChat.type);
    return `/elsnertech/${isChannel ? "channels" : "message"}/${lastActiveChat.id}`;
  }
  const defaultChannelId = localStorageUtil.getItem("defaultChannelId");
  return defaultChannelId ? `/elsnertech/channels/${defaultChannelId}` : null;
};

const RailBadge = ({ count }) =>
  count > 0 ? (
    <span className="ec-rail__badge">{count > 99 ? "99+" : count}</span>
  ) : null;

const AppRail = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lastChatPath = useRef(null);

  const railCounts = useBridgeStore((state) => state.railCounts);
  const railNotifications = useBridgeStore((state) => state.railNotifications);
  const setJumpConfig = useBridgeStore((state) => state.setJumpConfig);
  const theme = useBridgeStore((state) => state.theme);
  const setTheme = useBridgeStore((state) => state.setTheme);

  useEffect(() => {
    if (CHAT_PATH.test(pathname)) lastChatPath.current = pathname;
  }, [pathname]);

  const goTo = (path) => {
    if (!path) return;
    navigate(path);
    setJumpConfig({});
  };

  const navItems = [
    {
      key: "chats",
      label: "Chats",
      icon: <MessageOutlined />,
      isActive: CHAT_PATH.test(pathname),
      // A dot, not a count: the sidebar lists the unread chats
      dot: railCounts.unread > 0,
      onClick: () => goTo(lastChatPath.current || getFallbackChatPath()),
    },
    {
      key: "threads",
      label: "Threads",
      icon: <CommentOutlined />,
      isActive: pathname.startsWith("/elsnertech/unread/threads"),
      count: railCounts.threads,
      onClick: () => goTo("/elsnertech/unread/threads"),
    },
    {
      key: "drafts",
      label: "Drafts",
      icon: <FileTextOutlined />,
      isActive: pathname.startsWith("/elsnertech/draft"),
      count: railCounts.drafts,
      onClick: () => goTo("/elsnertech/draft"),
    },
  ];

  return (
    <nav className="ec-rail" aria-label="Main">
      <div className="ec-rail__logo" role="img" aria-label="eConnect">
        <img src={EconnectLogo} alt="" width={40} height={40} />
      </div>

      <div className="ec-rail__nav">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`ec-rail__item${item.isActive ? " is-active" : ""}`}
            onClick={item.onClick}
            aria-current={item.isActive ? "page" : undefined}
          >
            <span className="ec-rail__icon">
              {item.icon}
              {item.dot && <span className="ec-rail__dot" />}
              <RailBadge count={item.count} />
            </span>
            <span className="ec-rail__label">{item.label}</span>
          </button>
        ))}

        {/* Product notifications (HRMS, PMS, CRM, ...) open their in-app notification channel */}
        {railNotifications.length > 0 && <span className="ec-rail__divider" />}
        {railNotifications.map((channel) => {
          const name = channel.name || channel.notificationType || "Alerts";
          const path = `/elsnertech/notifications/${channel._id}`;
          const isActive = pathname === path;
          return (
            <button
              key={channel._id}
              type="button"
              className={`ec-rail__item${isActive ? " is-active" : ""}`}
              onClick={() => goTo(path)}
              aria-current={isActive ? "page" : undefined}
              title={`${name} notifications`}
            >
              <span className="ec-rail__icon">
                {getProductIcon(name)}
                <RailBadge count={channel.unreadCount} />
              </span>
              <span className="ec-rail__label">{name}</span>
            </button>
          );
        })}
      </div>

      <div className="ec-rail__footer">
        <Tooltip
          arrow={false}
          placement="right"
          title={theme === "dark" ? "Light mode" : "Dark mode"}
        >
          <button
            type="button"
            className="ec-rail__theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <SunOutlined /> : <MoonOutlined />}
          </button>
        </Tooltip>
        <div className="ec-rail__profile">
          <ProfileMenu placement="topLeft" avatarSize={36} />
        </div>
      </div>
    </nav>
  );
};

export default AppRail;
